import React from 'react';
import { ArrowUpRight, BriefcaseBusiness, MapPin, ShieldCheck } from 'lucide-react';
import { Button } from './components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';

const formatPay = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(amount);

export default function JobApplyDialog({ job, open, onOpenChange, onApplicationStarted }) {
  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="apply-dialog"
        className="w-[calc(100%-2rem)] max-w-lg gap-0 rounded-2xl border-[#DEDCD5] bg-[#FFFDF9] p-0 text-[#2C2825] shadow-[0_24px_80px_rgba(44,40,37,0.22)]"
      >
        <div className="border-b border-[#E6E3DC] px-5 py-5 pr-12 sm:px-6 sm:py-6 sm:pr-12">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#EEF5F0] px-3 py-1.5 text-xs font-semibold text-[#2F6B52]">
            <ShieldCheck className="h-4 w-4" strokeWidth={2} />
            Care Team application
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="font-serif text-3xl font-normal leading-tight tracking-[-0.02em]" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>
              Ready to apply?
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-6 text-[#69645F]">
              Review the assignment, then continue to Care Team Solutions to enter your information and consent.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 py-5 sm:px-6">
          <div className="rounded-2xl border border-[#E2E0DA] bg-white p-4">
            <h3 className="text-base font-semibold leading-snug text-[#2C2825]">{job.title}</h3>
            <div className="mt-3 grid gap-2 text-sm text-[#625E5A] sm:grid-cols-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-[#85817D]" strokeWidth={1.8} />
                {job.city}, {job.state}
              </div>
              <div className="flex items-center gap-2">
                <BriefcaseBusiness className="h-4 w-4 shrink-0 text-[#85817D]" strokeWidth={1.8} />
                {formatPay(job.weeklyGross)} gross / week
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-[#F4F1EB] p-4 text-sm leading-6 text-[#625E5A]">
            <strong className="font-semibold text-[#393532]">Your information stays with you here.</strong>{' '}
            HeyTraveler does not send your profile or claim that you applied. Care Team collects the application on its Staffing Referrals page.
          </div>
        </div>

        <DialogFooter className="gap-2 border-t border-[#E6E3DC] bg-[#F8F6F1] px-5 py-4 sm:px-6">
          <DialogClose asChild>
            <Button variant="outline" className="h-11 rounded-xl border-[#D8D6CF] bg-white px-5 shadow-none hover:bg-[#F2F0EA]">
              Not yet
            </Button>
          </DialogClose>
          <Button asChild className="h-11 rounded-xl bg-[#2C2825] px-5 text-white shadow-none hover:bg-[#1F1C1A]">
            <a
              href={job.applyUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="continue-care-team-application"
              onClick={() => {
                onApplicationStarted(job.id);
                onOpenChange(false);
              }}
            >
              Continue to Care Team <ArrowUpRight className="h-4 w-4" />
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
